import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Mail, Users as UsersIcon, Shirt, Image as ImageIcon,
  Calendar, Wallet, Gift, Check, Clock,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, BACKEND_URL } from "../lib/api";

const FEATURE_META = {
  rsvp: { icon: Mail, title: "Digital Invites & RSVP", subtitle: "Beautiful invites, RSVP tracking, meal choices and more.", member_route: "/dashboard/rsvps" },
  guests: { icon: UsersIcon, title: "Guest List Manager", subtitle: "Organize guests, plus-ones, seating, notes and more.", member_route: "/dashboard/guests" },
  "dress-tux": { icon: Shirt, title: "Dress & Tux Tracker", subtitle: "Track sizes, fittings, colors and pickup dates all in one place.", member_route: "/dashboard/dress-tux" },
  memories: { icon: ImageIcon, title: "Memory Vault", subtitle: "Collect, organize and download your precious memories.", member_route: "/dashboard/memories" },
  timeline: { icon: Calendar, title: "Planning & Timeline", subtitle: "Stay on track with checklists, tasks and important dates.", member_route: "/dashboard/timeline" },
  budget: { icon: Wallet, title: "Budget Tracker", subtitle: "Manage vendors, payments, deposits and balances.", member_route: "/dashboard/budget" },
  registry: { icon: Gift, title: "Registry & Funds", subtitle: "Cash funds, honeymoon funds, and external registries — all in one place.", member_route: "/dashboard/registry" },
};

function fmtMoney(n) {
  return "$" + (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function statusPill(s) {
  const map = {
    attending: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    declined: "bg-red-100 text-red-800",
    paid: "bg-green-100 text-green-800",
    deposit: "bg-amber-100 text-amber-800",
    unpaid: "bg-red-100 text-red-800",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${map[s] || "bg-gray-100 text-gray-700"}`}>{s}</span>;
}

export default function DemoFeaturePage() {
  const { feature } = useParams();
  const slug = "emmanuel-and-jennifer";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const meta = FEATURE_META[feature];

  useEffect(() => {
    setData(null);
    setErr(null);
    if (!meta) { setErr("Unknown feature"); return; }
    api.get(`/public/demo/${slug}/${feature}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load demo"));
  }, [feature, meta]);

  if (!meta) return <NotFound />;

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Navbar />

      {/* DEMO BAR */}
      <div className="bg-[var(--gold)] text-white text-center text-[12px] py-2 px-4">
        <span className="font-semibold uppercase tracking-[0.18em]">Demo Preview</span>
        <span className="mx-2">·</span>
        Real-life example from <strong>Emmanuel &amp; Jennifer</strong>'s wedding —{" "}
        <Link to="/signup" className="underline font-semibold" data-testid="demo-cta-signup">create your own</Link>
      </div>

      {/* HEADER */}
      <section className="section-pad bg-[var(--bg-warm)]">
        <div className="container-wide px-6 md:px-12">
          <Link to="/" className="text-sm text-[var(--muted)] flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
          <div className="flex items-start gap-5 max-w-3xl">
            <div className="w-14 h-14 rounded-full border border-[var(--gold)] flex items-center justify-center shrink-0">
              <meta.icon className="w-6 h-6 text-[var(--gold)]" strokeWidth={1.4} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Feature Demo</p>
              <h1 className="font-serif font-light text-4xl md:text-6xl leading-tight">{meta.title}</h1>
              <p className="text-[var(--muted)] mt-3">{meta.subtitle}</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/signup" className="btn-gold" data-testid="demo-create-site">Create your own <ArrowRight className="w-4 h-4" /></Link>
                <Link to={`/site/${slug}`} className="btn-outline-gold">Visit demo wedding</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section-pad">
        <div className="container-wide px-6 md:px-12">
          {err && <p className="text-red-500 text-center">{err}</p>}
          {!data && !err && <p className="text-center text-[var(--muted)]">Loading…</p>}
          {data && feature === "rsvp" && <RsvpDemo data={data} />}
          {data && feature === "guests" && <GuestsDemo data={data} />}
          {data && feature === "dress-tux" && <DressTuxDemo data={data} />}
          {data && feature === "memories" && <MemoriesDemo data={data} />}
          {data && feature === "timeline" && <TimelineDemo data={data} />}
          {data && feature === "budget" && <BudgetDemo data={data} />}
          {data && feature === "registry" && <RegistryDemo data={data} />}
        </div>
      </section>

      {/* MEMBER CTA */}
      <section className="bg-[var(--ink)] text-white py-14">
        <div className="container-wide px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-script text-[var(--gold)] text-3xl">Like what you see?</p>
            <p className="text-white/70">Every feature you've explored is yours when you sign up. Same beauty, your love story.</p>
          </div>
          <Link to="/signup" className="btn-gold" data-testid="demo-bottom-cta">Get Started Free <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="font-serif text-3xl">Feature demo not found</p>
        <Link to="/" className="btn-gold mt-6">Back to home</Link>
      </div>
    </div>
  );
}

function StatRow({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
      {items.map((s) => (
        <div key={s.l} className="bg-white border border-[var(--line)] rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2">{s.l}</p>
          <p className="font-serif text-3xl">{s.v}</p>
        </div>
      ))}
    </div>
  );
}

function RsvpDemo({ data }) {
  const { guests, stats, wedding } = data;
  return (
    <>
      <StatRow items={[
        { l: "Total Invited", v: stats.total },
        { l: "Attending", v: stats.attending },
        { l: "Pending", v: stats.pending },
        { l: "Declined", v: stats.declined },
      ]} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[var(--line)] rounded-2xl p-6">
          <p className="font-serif text-2xl mb-4">Live RSVP feed</p>
          <div className="divide-y divide-[var(--line)]">
            {guests.slice(0, 12).map((g) => (
              <div key={g.guest_id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--bg-warm)] flex items-center justify-center text-[var(--gold)] font-serif">{g.name?.[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{g.name}</p>
                  <p className="text-xs text-[var(--muted)]">
                    {g.plus_ones ? `+${g.plus_ones} guest` : "Solo"}
                    {g.meal_preference && ` · ${g.meal_preference}`}
                  </p>
                </div>
                {statusPill(g.rsvp_status)}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[var(--bg-warm)] rounded-2xl p-6">
          <p className="font-serif text-2xl mb-2">Public RSVP page</p>
          <p className="text-sm text-[var(--muted)] mb-5">Each guest gets a unique link. They click, RSVP, and pick a meal — done.</p>
          <Link to={`/site/${wedding.slug}`} className="btn-outline-gold w-full justify-center">Open {wedding.couple_names}'s site</Link>
          <div className="mt-6 p-4 bg-white rounded-xl">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Real-time meal counts</p>
            <ul className="text-sm space-y-1">
              <li className="flex justify-between"><span>Chicken</span><span>{guests.filter(g => g.meal_preference === "Chicken").length}</span></li>
              <li className="flex justify-between"><span>Vegetarian</span><span>{guests.filter(g => /veget/i.test(g.meal_preference || "")).length}</span></li>
              <li className="flex justify-between"><span>Vegan</span><span>{guests.filter(g => /vegan/i.test(g.meal_preference || "")).length}</span></li>
              <li className="flex justify-between"><span>Gluten-free</span><span>{guests.filter(g => /gluten/i.test(g.meal_preference || "")).length}</span></li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

function GuestsDemo({ data }) {
  const { guests, stats } = data;
  return (
    <>
      <StatRow items={[
        { l: "Total Guests", v: stats.total },
        { l: "Attending", v: stats.attending },
        { l: "Plus Ones", v: guests.reduce((acc, g) => acc + (g.plus_ones || 0), 0) },
        { l: "Pending RSVP", v: stats.pending },
      ]} />
      <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-warm)] text-[var(--muted)]">
            <tr>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Name</th>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">RSVP</th>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Meal</th>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Plus Ones</th>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--line)]">
            {guests.map((g) => (
              <tr key={g.guest_id}>
                <td className="py-3 px-4">{g.name}</td>
                <td className="py-3 px-4">{statusPill(g.rsvp_status)}</td>
                <td className="py-3 px-4 text-[var(--muted)]">{g.meal_preference || "—"}</td>
                <td className="py-3 px-4">{g.plus_ones || 0}</td>
                <td className="py-3 px-4 text-[var(--muted)] text-xs">{g.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DressTuxDemo({ data }) {
  const { outfits } = data;
  const totalCost = outfits.reduce((acc, o) => acc + (o.total_cost || 0), 0);
  const totalPaid = outfits.reduce((acc, o) => acc + (o.paid_amount || 0), 0);
  return (
    <>
      <StatRow items={[
        { l: "Wedding Party", v: outfits.length },
        { l: "Total Budget", v: fmtMoney(totalCost) },
        { l: "Paid", v: fmtMoney(totalPaid) },
        { l: "Remaining", v: fmtMoney(totalCost - totalPaid) },
      ]} />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {outfits.map((o) => (
          <div key={o.outfit_id} className="bg-white border border-[var(--line)] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--gold)]">{o.role.replaceAll("_", " ")}</p>
              {statusPill(o.payment_status)}
            </div>
            <p className="font-serif text-xl">{o.member_name}</p>
            <p className="text-sm text-[var(--muted)] mt-1 capitalize">{o.garment_type} · Size {o.size} · {o.color}</p>
            <p className="text-xs text-[var(--muted)] mt-3"><strong>Designer:</strong> {o.designer || o.vendor}</p>
            {o.fitting_date && <p className="text-xs text-[var(--muted)]"><strong>Fitting:</strong> {o.fitting_date}</p>}
            <div className="flex justify-between text-sm mt-3">
              <span className="text-[var(--muted)]">Cost</span>
              <span className="font-medium">{fmtMoney(o.total_cost)}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function MemoriesDemo({ data }) {
  const { memories } = data;
  // If no memories, show curated stock samples
  const fallback = [
    "https://images.pexels.com/photos/33403878/pexels-photo-33403878.jpeg?w=600",
    "https://images.unsplash.com/photo-1718997534125-052b2ff48cd0?w=600",
    "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=600",
    "https://images.pexels.com/photos/11296094/pexels-photo-11296094.jpeg?w=600",
    "https://images.unsplash.com/photo-1525258801032-ed95063bf83e?w=600",
    "https://images.unsplash.com/photo-1484876632310-ddb3b48133cc?w=600",
    "https://images.unsplash.com/photo-1712932115370-4f9d354ced6b?w=600",
    "https://images.unsplash.com/photo-1705592388411-f5d150296d1c?w=600",
  ];
  const photos = memories.length ? memories.map((m) => m.storage_path?.startsWith("http") ? m.storage_path : `${BACKEND_URL}/api/files/${m.storage_path}`) : fallback;
  return (
    <>
      <StatRow items={[
        { l: "Approved Photos", v: photos.length },
        { l: "Pending Review", v: 12 },
        { l: "Storage Used", v: "2.4 GB" },
        { l: "Privacy", v: "Private" },
      ]} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((src, i) => (
          <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-[var(--bg-warm)]">
            <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </div>
        ))}
      </div>
    </>
  );
}

function TimelineDemo({ data }) {
  const { milestones, stats } = data;
  const pct = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;
  return (
    <>
      <StatRow items={[
        { l: "Milestones", v: milestones.length },
        { l: "Total Tasks", v: stats.total },
        { l: "Completed", v: stats.completed },
        { l: "Progress", v: `${pct}%` },
      ]} />
      <div className="space-y-6">
        {milestones.map((m) => (
          <div key={m.milestone} className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[var(--gold)]" />
                <p className="font-serif text-2xl">{m.milestone}</p>
              </div>
              <p className="text-xs text-[var(--muted)]">{m.tasks.filter(t => t.completed || t.is_complete).length} / {m.tasks.length}</p>
            </div>
            <ul className="space-y-2">
              {m.tasks.map((t) => {
                const done = t.completed || t.is_complete;
                return (
                  <li key={t.task_id} className="flex items-center gap-3 text-sm">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[var(--gold)] text-white" : "border border-[var(--line)]"}`}>
                      {done && <Check className="w-3 h-3" />}
                    </div>
                    <span className={done ? "line-through text-[var(--muted)]" : ""}>{t.title}</span>
                    <span className="ml-auto text-xs text-[var(--muted)]">{t.due_date}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

function BudgetDemo({ data }) {
  const { items, totals } = data;
  const pct = totals.budgeted ? Math.round((totals.paid / totals.budgeted) * 100) : 0;
  return (
    <>
      <StatRow items={[
        { l: "Total Budget", v: fmtMoney(totals.budgeted) },
        { l: "Paid", v: fmtMoney(totals.paid) },
        { l: "Remaining", v: fmtMoney(totals.remaining) },
        { l: "Progress", v: `${pct}%` },
      ]} />
      <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-warm)] text-[var(--muted)]">
            <tr>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Category</th>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Vendor</th>
              <th className="text-right py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Cost</th>
              <th className="text-right py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Paid</th>
              <th className="text-right py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Balance</th>
              <th className="text-left py-3 px-4 font-medium text-[11px] uppercase tracking-[0.18em]">Due</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--line)]">
            {items.map((b) => (
              <tr key={b.item_id}>
                <td className="py-3 px-4 font-medium">{b.category}</td>
                <td className="py-3 px-4 text-[var(--muted)]">{b.vendor}</td>
                <td className="py-3 px-4 text-right">{fmtMoney(b.total_cost)}</td>
                <td className="py-3 px-4 text-right text-green-700">{fmtMoney(b.paid_amount)}</td>
                <td className="py-3 px-4 text-right">{fmtMoney(b.total_cost - b.paid_amount)}</td>
                <td className="py-3 px-4 text-xs text-[var(--muted)]">{b.due_date || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RegistryDemo({ data }) {
  const { items } = data;
  const funds = items.filter((i) => i.type !== "external_link");
  const links = items.filter((i) => i.type === "external_link");
  return (
    <>
      <StatRow items={[
        { l: "Cash Funds", v: funds.length },
        { l: "External Lists", v: links.length },
        { l: "Total Raised", v: fmtMoney(funds.reduce((a, f) => a + (f.raised_amount || 0), 0)) },
        { l: "Goal", v: fmtMoney(funds.reduce((a, f) => a + (f.target_amount || 0), 0)) },
      ]} />
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {funds.map((f) => {
          const pct = f.target_amount ? Math.round((f.raised_amount / f.target_amount) * 100) : 0;
          return (
            <div key={f.item_id} className="bg-white border border-[var(--line)] rounded-2xl p-6">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">{f.type.replaceAll("_", " ")}</p>
              <p className="font-serif text-2xl mb-3">{f.name}</p>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[var(--muted)]">Raised</span>
                <span className="font-medium">{fmtMoney(f.raised_amount)} of {fmtMoney(f.target_amount)}</span>
              </div>
              <div className="h-2 bg-[var(--bg-warm)] rounded-full overflow-hidden">
                <div className="h-full bg-[var(--gold)] rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <p className="text-xs text-[var(--muted)] mt-2">{pct}% funded</p>
            </div>
          );
        })}
      </div>
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6">
        <p className="font-serif text-2xl mb-4">External registries</p>
        <ul className="space-y-3">
          {links.map((l) => (
            <li key={l.item_id} className="flex items-center justify-between">
              <span className="text-sm">{l.name}</span>
              <a href={l.url} target="_blank" rel="noreferrer" className="text-[var(--gold)] text-sm hover:underline">Visit →</a>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
